import { Component, OnInit, ViewChild, ElementRef } from '@angular/core';
import {Select, Store} from '@ngxs/store';
import { DetailsQueries } from '../../queries/details.queries';
import {
  AuctionItem,
  AuctionService,
  ImageMetaData,
  LaunchDarklyService,
  UserPreferences,
  BookValueSpreadProviderName
} from 'remarketing-angular-library';
import { DetailsHeaderQuery } from '../../interfaces/detail-header.query';
import { Observable } from 'rxjs';
import { BsModalRef, BsModalService } from 'ngx-bootstrap/modal';
import { AnimatedNotificationComponent } from '../animated-notification/animated-notification.component';
import { get } from 'lodash';
import { NgxPrintDirective, NgxPrintModule } from 'ngx-print';
import { map, take, takeUntil, tap } from 'rxjs/operators';
import { ActivatedRoute, Router } from '@angular/router';
import { InventoryItemUtilService } from '../../../../../../src/sell/lib/sell-shared/services/inventory-item-util.service';
import { TranslocoService, TranslocoDirective } from '@jsverse/transloco';
import { SaComponent } from 'smartauction-frontend-micro-library';
import { PrintVdpSellComponent } from '../print-vdp-sell/print-vdp-sell.component';
import { ConditionIndicatorComponent, VehicleDisclosuresIndicatorComponent, DatalistItemComponent } from 'remarketing-ui-components-lib';
import { ClipboardModule } from 'ngx-clipboard';
import { NgTemplateOutlet, AsyncPipe, DecimalPipe } from '@angular/common';


@Component({
    selector: 'lib-details-header',
    templateUrl: './details-header.component.html',
    styleUrls: ['./details-header.component.scss'],
    imports: [TranslocoDirective, ClipboardModule, ConditionIndicatorComponent, VehicleDisclosuresIndicatorComponent, DatalistItemComponent, NgxPrintModule, NgTemplateOutlet, PrintVdpSellComponent, AsyncPipe, DecimalPipe]
})
export class DetailsHeaderComponent extends SaComponent implements OnInit {
  @Select(DetailsQueries.images) images$: Observable<ImageMetaData[]>;
  @Select(DetailsQueries.header) header$: Observable<DetailsHeaderQuery>;
  @ViewChild('copyVinButton') copyVinButton: ElementRef;
  userPreferences$: Observable<UserPreferences> = this.store
    .select(state => state.user.userProfile)
    .pipe(
      map(profile => get(profile, 'userPreferences')),
      tap(preferences => {
        const defaultBookProvider = get(preferences, 'defaultBookProvider.providerName', 'BlackBook');
        this.tabSelected = defaultBookProvider.replace(/\s+/g, '');
      })
    );

  @ViewChild(NgxPrintDirective) ngxPrintDirective: NgxPrintDirective;
  showPrintContent = false;

  modalRef: BsModalRef;
  modalConfig: any;
  tabSelected: string;
  blackBookValues: any;
  nadaValues: any;
  bookValue = 0;
  header: DetailsHeaderQuery;
  images: ImageMetaData[] = [];

  auctionItem: AuctionItem;
  showLoader: boolean;

  detailsHeaderTranslations = {
    distanceUnit: ''
  }
  conditionIndicatorTranslations = {};

  onToggleBookValues(event) {
    event.preventDefault();
    this.tabSelected = event.target.dataset.selectedTab;
  }

  constructor(
    private route: ActivatedRoute,
    private auctionService: AuctionService,
    private router: Router,
    public modalService: BsModalService,
    private store: Store,
    private readonly _launchDarklyService: LaunchDarklyService,
    private readonly inventoryItemUtilService: InventoryItemUtilService,
    private readonly translocoService: TranslocoService
  ) {
    super();
  }

  get vehicleInfo() {
    return this.header;
  }

  ngOnInit() {
    this.getTranslations();
    this.header = this.store.selectSnapshot(DetailsQueries.header);
    this.setDefaultBookValue();
    this.getAuctionItem();
  }

  get showEmailIcon(): boolean {
    return this._launchDarklyService.getFeatureFlag('vdp-emailEnabled', false);
  }

  private openModal(template, initialState) {
    const body = document.getElementsByTagName('body'); // ngBootstrap bug workaround
    this.modalConfig = {
      class: 'modal--animated-notification',
      backdrop: false,
      initialState
    };
    this.modalRef = this.modalService.show(template, this.modalConfig);
    this.modalRef.setClass('modal--animated-notification');
    setTimeout(() => {
      this.modalRef.hide();
      // ngBootstrap bug workaround
      body[0].classList.remove('modal-open');
      body[0].style.removeProperty('overflow-y');
    }, 5000);
    this.copyVinButton.nativeElement.focus();
  }

  copied() {
    this.openModal(AnimatedNotificationComponent, { message: 'VIN Copied' });
  }

  getTenantNameFromStore() {
    return this.store.selectSnapshot((state => get (state, 'configuration.configuration.tenantName')));
  }

  goToEmail(inventoryItemUuid: string) {
    const link  = '../ui/vehicles/email?inventoryItem=' + inventoryItemUuid;
    window.open(link);
  }

  printVDP() {
    this.showPrintContent = true;
    this.bookValue = this.inventoryItemUtilService.defaultBookValue;
    setTimeout(() => {
      this.ngxPrintDirective.print();
    }, 1000);
  }

  getAuctionItem() {
    this.showLoader = true;
    const type = 'INVENTORY_ITEM';

    this.auctionService.getAuctionItem( this.header?.inventoryItemUuid, type )
      .pipe( take(1) )
      .subscribe(
        auctionItem => this.updateAuctionItem( auctionItem ),
        error       => this.router.navigate([ '/404' ]),
      );
  }

  updateAuctionItem(auctionItem) {
    this.auctionItem = auctionItem;

    // massage data to be better accessible in template
    let blackBookValuesRaw;
    let nadaValuesRaw;

    this.auctionItem.bookValues.forEach(item => {
      if (item.providerName === BookValueSpreadProviderName.BLACK_BOOK) {
        blackBookValuesRaw = item.values;
      }
      if ((item.providerName === BookValueSpreadProviderName.NADA) ||(item.providerName === BookValueSpreadProviderName.JD_POWER)) {
        nadaValuesRaw = item.values;
      }
    });
    if (blackBookValuesRaw) {
      this.blackBookValues = blackBookValuesRaw.reduce((accumalator, currentValue) => {
        const newKey = currentValue.providerValue.replace(/\s+/g, '');
        accumalator[newKey] = {
          ...currentValue
        };
        return accumalator;
      }, {});
    }
    if (nadaValuesRaw) {
      this.nadaValues = nadaValuesRaw.reduce((accumalator, currentValue) => {
        const newKey = currentValue.providerValue.replace(/\s+/g, '');
        accumalator[newKey] = {
          ...currentValue
        };
        return accumalator;
      }, {});
    }
  }

  setDefaultBookValue() {
    this.store
      .select(state => state.user.userProfile.userPreferences.defaultBookProvider.providerName)
      .pipe(takeUntil(this.destroyed$))
      .subscribe(bookProviderName => {
        if (bookProviderName && bookProviderName === 'NADA') {
          this.bookValue = 1;
        }
      });
  }

  getTranslations() {

    const keyParams = {
      'common': {},
      'UIComponents': {}
    };

    this.translocoService.selectTranslateObject(keyParams).pipe(takeUntil(this.destroyed$)).subscribe((translate: any[]) => {
      this.detailsHeaderTranslations.distanceUnit = translate[0].distanceUnit;
      this.conditionIndicatorTranslations = translate[1].conditionIndicatorTranslations;
    }, error => {
      console.log(error);
    });
  }

  get emailImageIconSrc(): string {
    return `../../../ui/assets/assets-${this.getTenantNameFromStore()}/${this.getTenantNameFromStore()}-envelope.svg`;
  }
}
